import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserRoles } from 'app/hooks';
import { Button } from '@/components/ui/button';
import { PreviewIframe } from '../../components/PreviewIframe';

import { ONBOARDING_PATHS } from './constants/paths';
import { OnboardingLayout } from './components/OnboardingLayout';
import { UpgradeModal, type UpgradeData } from './components/UpgradeModal';
import { useUpgradeWebsitePlan } from './hooks/useUpgradeWebsitePlan';
import { useWebBuilderAccess } from '../../hooks/useWebBuilderAccess';
import { useSubscriptionPricing } from '../../hooks/useSubscription';
import { useOnboarding } from './store/onboarding-store';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';
import { useNotifyOnboardingPreview } from './hooks/useNotifyOnboardingPreview';

const PREVIEW_BASE_DOMAIN = import.meta.env.VITE_PREVIEW_BASE_DOMAIN as string | undefined;
const ONBOARDING_PREVIEW_WEBSITE_ID = import.meta.env.VITE_ONBOARDING_PREVIEW_WEBSITE as
  | string
  | undefined;
const PREVIEW_URL =
  PREVIEW_BASE_DOMAIN && ONBOARDING_PREVIEW_WEBSITE_ID
    ? `https://${ONBOARDING_PREVIEW_WEBSITE_ID}.${PREVIEW_BASE_DOMAIN}/home-preview`
    : undefined;

const PreviewStep = (): ReactElement | null => {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const { primaryColor, logoUrl, previewNotified, setPreviewNotified } = useOnboarding();
  const { isPharmacyAdmin, isSupport } = useUserRoles();
  const { trackSiteGenerated, trackCheckoutStarted, trackCheckoutCompleted } =
    useOnboardingTracker();
  const { hasWebBuilderAccess, isLoading } = useWebBuilderAccess();
  const { mutateAsync: upgradePlan } = useUpgradeWebsitePlan();
  const { data: pricing } = useSubscriptionPricing();
  const { mutate: notifyOnboardingPreview } = useNotifyOnboardingPreview();

  const homePageStep = { pathname: ONBOARDING_PATHS.homepage, search: searchParams.toString() };

  // Slack notification + tracking fire once for upsell users viewing the preview for the first time.
  const notifiedRef = useRef(previewNotified);
  useEffect(() => {
    if (notifiedRef.current || isLoading || hasWebBuilderAccess) return;
    notifiedRef.current = true;
    setPreviewNotified();
    trackSiteGenerated();
    notifyOnboardingPreview(organisationId);
  }, [
    isLoading,
    hasWebBuilderAccess,
    setPreviewNotified,
    trackSiteGenerated,
    notifyOnboardingPreview,
    organisationId,
  ]);

  if (isLoading) return null;

  const handleUpgrade = async ({ billing, goLiveDate }: UpgradeData) => {
    if (!pricing) return;
    const amount = billing === 'annual' ? pricing.annual : pricing.monthly;
    await upgradePlan({
      organisationId,
      billing,
      price: { amount, currency: pricing.currency },
      goLiveDate,
    });
    trackCheckoutCompleted({
      billing,
      price: amount,
      goLiveDate: goLiveDate?.toISOString(),
    });
  };

  const handleUpgradeComplete = () => {
    setUpgradeOpen(false);
    navigate(homePageStep);
  };

  const handleContinue = () => {
    if (hasWebBuilderAccess) {
      navigate(homePageStep);
    } else {
      trackCheckoutStarted();
      setUpgradeOpen(true);
    }
  };

  const primaryLabel = hasWebBuilderAccess ? 'Continue' : 'Upgrade and continue';

  return (
    <OnboardingLayout
      backTo={ONBOARDING_PATHS.logo}
      primaryLabel={primaryLabel}
      onPrimary={handleContinue}
    >
      <div className="vtwo:text-center vtwo:mb-6">
        <h1 className="vtwo:text-3xl vtwo:font-bold vtwo:mb-3">
          Here's what your site could look like
        </h1>
        <p className="vtwo:text-sm vtwo:text-gray-600 vtwo:max-w-2xl vtwo:mx-auto vtwo:mb-4">
          We've built this preview using your brand colours and logo. The content and layout are
          just examples — you'll have full control to edit everything once your site is live.
        </p>
        {!hasWebBuilderAccess && (
          <Button
            size="lg"
            onClick={handleContinue}
            className="vtwo:bg-featured vtwo:hover:bg-featured-hover vtwo:rounded-full"
          >
            Upgrade and continue
          </Button>
        )}
      </div>

      <div className="vtwo:rounded-xl vtwo:overflow-hidden vtwo:bg-white vtwo:shadow-lg vtwo:border vtwo:border-gray-200 vtwo:h-[600px]">
        <PreviewIframe
          src={PREVIEW_URL}
          autoScroll
          colorPreview={primaryColor || undefined}
          logoPreview={logoUrl || undefined}
        />
      </div>

      <UpgradeModal
        isAdmin={isPharmacyAdmin || isSupport}
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        onSubmit={handleUpgrade}
        onComplete={handleUpgradeComplete}
        pricing={pricing}
      />
    </OnboardingLayout>
  );
};

export default PreviewStep;
